/**
 * PromptEditorView
 *
 * Prompt editor with breadcrumbs matching the detail view layout,
 * version header (version + badge + Save), instruction text,
 * then a split pane (editor left, preview right) filling to the bottom.
 *
 * Editor is CodeMirror 6 with YAML syntax, line numbers, folding, and a
 * custom completion source for `{taxonomy}` / `{brand_name}` / signal
 * placeholders driven by the /template-variables API.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CodeMirror from '@uiw/react-codemirror';
import { yaml as cmYaml } from '@codemirror/lang-yaml';
import {
  autocompletion,
  CompletionContext,
  CompletionResult,
} from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';
import { Button } from '../../../../ui/button';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '../../../../ui/breadcrumb';
import {
  usePromptDetail,
  useUpdatePromptDraft,
  useTemplateVariables,
  usePromptPreview,
} from '../../hooks/usePrompts';
import type { TemplateVariablesResponse } from '../../types/prompt';
import VersionBadge from '../../shared/VersionBadge';
import PromptPreviewPane from './PromptPreviewPane';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Build a CodeMirror completion source that fires inside `{...}` placeholder
 * tokens. Mirrors the old TemplateAutocomplete rules:
 *   - Only a single `{` (not `{{`).
 *   - Suppressed inside `output_format:` blocks where `{` is part of a JSON
 *     schema example.
 */
function makeVariableCompletions(variables: TemplateVariablesResponse | undefined) {
  // Only `built_in` carries real completion options — the renderer
  // substitutes exactly `{taxonomy}` and `{brand_name}`, nothing else.
  const unique = variables?.built_in ?? [];

  return (context: CompletionContext): CompletionResult | null => {
    // Match `{` followed by optional word chars up to the cursor.
    const match = context.matchBefore(/\{[A-Za-z_]\w*|\{/);
    if (!match) return null;

    // Reject doubled `{{` (legacy Jinja style) so CodeMirror doesn't pop.
    const charBefore = context.state.doc.sliceString(
      Math.max(0, match.from - 1),
      match.from
    );
    if (charBefore === '{') return null;

    // Suppress inside `output_format:` blocks — JSON schema examples live
    // there and every `{` is literal, not a placeholder.
    //
    // Walk backwards through lines to find the nearest COLUMN-0 key. The
    // `^[A-Za-z_]` anchor rejects any indented line, so we only ever land
    // on a top-level YAML key.
    //
    // FLAT-KEY ASSUMPTION: v8 prompts are flat — none of the required
    // content fields contain nested mappings. If a future prompt introduces
    // e.g. a nested `examples:` map with its own keys, this heuristic
    // will skip past them and find the wrong parent. When that happens,
    // replace this walker with the CodeMirror YAML syntax tree or a real
    // YAML scanner that understands indentation-scoped nesting.
    const doc = context.state.doc.toString();
    const posBefore = doc.slice(0, match.from);
    const lines = posBefore.split('\n');
    let currentKey: string | null = null;
    for (let i = lines.length - 1; i >= 0; i--) {
      // ^[A-Za-z_] pins this to column 0 — indented lines never match.
      const m = lines[i].match(/^([A-Za-z_][\w-]*)\s*:/);
      if (m) {
        currentKey = m[1];
        break;
      }
    }
    if (currentKey === 'output_format') return null;

    // Don't fire if we're in the middle of an already-closed `{var}`.
    const afterMatch = context.state.doc.sliceString(
      context.pos,
      Math.min(doc.length, context.pos + 1)
    );
    const closeOffset = afterMatch === '}' ? 1 : 0;

    return {
      from: match.from,
      to: context.pos + closeOffset,
      options: unique.map((v) => ({
        label: `{${v.name}}`,
        displayLabel: v.name,
        detail: v.description || undefined,
        type: 'variable',
        apply: `{${v.name}}`,
      })),
      validFor: /^\{?[A-Za-z_]\w*$/,
    };
  };
}

interface PromptEditorViewProps {
  versionId: string;
  taxonomyDisplayName?: string;
  onBack: () => void;
}

const PromptEditorView: React.FC<PromptEditorViewProps> = ({
  versionId,
  taxonomyDisplayName,
  onBack,
}) => {
  const { data: version, isError, refetch } = usePromptDetail(versionId);
  const updateDraft = useUpdatePromptDraft();
  const { data: templateVars } = useTemplateVariables();

  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Debounce preview content to avoid re-rendering on every keystroke
  const debouncedContent = useDebouncedValue(content, 300);

  // Server-rendered preview — same renderer the classification pipeline
  // uses, so what the author sees here is exactly what the LLM will get.
  // No client-side YAML parsing or hardcoded field lists.
  const {
    data: previewData,
    isLoading: previewLoading,
    isError: previewIsError,
    error: previewError,
  } = usePromptPreview(
    debouncedContent,
    version?.taxonomyKey ?? '',
    (version?.intelligenceRefs as { dimension?: string } | null)?.dimension ?? null,
  );

  useEffect(() => {
    if (version?.templateContent) {
      setContent(version.templateContent);
      setHasUnsaved(false);
    }
  }, [version?.templateContent]);

  // Warn on browser navigation (tab close, reload) with unsaved changes
  useEffect(() => {
    if (!hasUnsaved) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  // Rebuild the completion source when the variable list arrives. Stable
  // reference per-variables-change so CodeMirror reconfigures cleanly.
  const cmExtensions = useMemo(
    () => [
      cmYaml(),
      autocompletion({ override: [makeVariableCompletions(templateVars)] }),
      EditorView.lineWrapping,
    ],
    [templateVars]
  );

  const handleCmChange = useCallback((value: string) => {
    setContent(value);
    setHasUnsaved(true);
    setSaveError(null);
  }, []);

  const handleSave = () => {
    if (!content.trim()) return;
    setSaveError(null);
    updateDraft.mutate(
      { versionId, data: { templateContent: content } },
      {
        onSuccess: () => {
          setHasUnsaved(false);
          setSaveError(null);
        },
        onError: (err: unknown) => {
          const message =
            err instanceof Error && err.message
              ? err.message
              : 'Failed to save draft.';
          setSaveError(message);
        },
      }
    );
  };

  const handleBack = () => {
    if (hasUnsaved && !window.confirm('You have unsaved changes. Discard and leave?')) {
      return;
    }
    onBack();
  };

  /** Exit the editor and navigate somewhere else (breadcrumb clicks).
   *  The destination URL has no `?edit=` param, so the parent's
   *  URL-driven editor state naturally clears on navigation. */
  const handleExit = (to: string) => {
    if (hasUnsaved && !window.confirm('You have unsaved changes. Discard and leave?')) {
      return;
    }
    navigate(to);
  };

  if (isError) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-[50vh] gap-3">
        <p className="text-gray-500">Failed to load prompt version.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        <Button variant="ghost" size="sm" onClick={onBack}>Go back</Button>
      </div>
    );
  }

  if (!version) {
    return (
      <div className="p-6 flex items-center justify-center h-[50vh]">
        <p className="text-gray-500">Loading editor...</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border border-border/70 bg-card p-6 shadow-[var(--shadow-card)]">
      {/* Breadcrumbs — same position as detail view */}
      <div className="mb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                className="cursor-pointer"
                onClick={() => handleExit('/v4/go-lab/intelligence/prompts')}
              >
                Prompts
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                className="cursor-pointer"
                onClick={() =>
                  handleExit(`/v4/go-lab/intelligence/prompts?taxonomyKey=${version.taxonomyKey}`)
                }
              >
                {taxonomyDisplayName || version.taxonomyKey}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink className="cursor-pointer" onClick={handleBack}>
                {version.displayName}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Draft v{version.versionNumber}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Version header */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold">v{version.versionNumber}</h2>
        <VersionBadge status={version.status} />
      </div>

      {/* Save row — same position as "View Timeline" relative to content */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500">
          Edit the prompt template below. Save when ready.
        </p>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!hasUnsaved || updateDraft.isPending}
        >
          {updateDraft.isPending ? 'Saving...' : hasUnsaved ? 'Save' : 'Saved'}
        </Button>
      </div>
      {saveError && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <span className="font-semibold">Save failed:</span>{' '}
          <span className="whitespace-pre-wrap font-mono text-xs">{saveError}</span>
        </div>
      )}

      {/* Split pane — fills remaining height */}
      <div className="flex-1 flex overflow-hidden border rounded-lg min-h-0">
        {/* Left: CodeMirror editor */}
        <div className="flex-1 border-r min-h-0 overflow-hidden">
          <CodeMirror
            value={content}
            height="100%"
            className="h-full text-sm"
            extensions={cmExtensions}
            onChange={handleCmChange}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLine: true,
              highlightActiveLineGutter: true,
              bracketMatching: true,
              closeBrackets: true,
              autocompletion: true,
              searchKeymap: true,
            }}
          />
        </div>

        {/* Right: Preview — same server-rendered output the detail view shows */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Preview
          </h4>
          <PromptPreviewPane
            sections={previewData?.sections}
            isLoading={previewLoading && !!debouncedContent}
            error={previewIsError ? previewError : null}
            emptyMessage="Start typing to see preview."
          />
        </div>
      </div>
    </div>
  );
};

export default PromptEditorView;
